import { useCallback, useEffect, useRef, useState } from 'react';

import GetSessionRtmp from '@/connection/streams/get-session-rtmp';
import { Session } from '@/types/session.types';
import { niceTime } from '@/utils/dates';
import Modal from '@ui/modal/modal';
import Container from '@ui/container/container';
import Icon from '@ui/icons/icon';
import Text from '@ui/text/text/text';
import { ICONS } from '@ui/icons/types';
import Button from '@ui/buttons/button/button';
import MaskedValueContainer from '@/micros/edit-project/controls/masked-value-container';
import { MaskedValueType } from '@/micros/edit-project/controls/container-types';

type StreamingCredentialsModalProps = {
  open: boolean;
  handleClose: () => void;
  session?: Session | null;
  language: string;
};

const StreamingCredentialsModal = ({ open, handleClose, session, language }: StreamingCredentialsModalProps) => {
  const [rtmpInfo, setRtmpInfo] = useState({
    rtmpsAddress: '',
    streamKey: '',
    expiration: '',
  });
  const [loadingRtmps, setLoadingRtmps] = useState(true);
  const [copiedContent, setCopiedContent] = useState({ rtmpsAddress: false, streamKey: false });

  const copyRtmpsAddressTimeout = useRef<NodeJS.Timeout | null>(null);
  const copyStreamKeyTimeout = useRef<NodeJS.Timeout | null>(null);

  const revealRtmps = useCallback(async () => {
    if (!session) return;
    try {
      const { rtmps_address, stream_key, expire_at } = await GetSessionRtmp({
        sessionUuid: session.uuid,
        language: session.streaming_options.single_stream ? session.default_language : language,
      });

      setRtmpInfo({
        rtmpsAddress: rtmps_address,
        streamKey: stream_key,
        expiration: niceTime(expire_at),
      });
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingRtmps(false);
    }
  }, [language, session]);

  const delayResetForAnimationRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (open) {
      revealRtmps();
    } else {
      if (delayResetForAnimationRef.current) {
        clearTimeout(delayResetForAnimationRef.current);
      }
      delayResetForAnimationRef.current = setTimeout(() => {
        setRtmpInfo({
          rtmpsAddress: '',
          streamKey: '',
          expiration: '',
        });
        setLoadingRtmps(true);
      }, 300);
    }

    return () => {
      if (delayResetForAnimationRef.current) {
        clearTimeout(delayResetForAnimationRef.current);
      }
    };
  }, [open, revealRtmps]);

  const copyRtmpsAddress = () => {
    if (!rtmpInfo.rtmpsAddress) return;

    if (copyRtmpsAddressTimeout.current) {
      clearTimeout(copyRtmpsAddressTimeout.current);
    }

    navigator.clipboard.writeText(rtmpInfo.rtmpsAddress);

    setCopiedContent((prev) => ({ ...prev, rtmpsAddress: true }));

    copyRtmpsAddressTimeout.current = setTimeout(() => {
      setCopiedContent((prev) => ({ ...prev, rtmpsAddress: false }));
    }, 2000);
  };

  const copyPassword = () => {
    if (!rtmpInfo.streamKey) return;

    if (copyStreamKeyTimeout.current) {
      clearTimeout(copyStreamKeyTimeout.current);
    }

    navigator.clipboard.writeText(rtmpInfo.streamKey);

    setCopiedContent((prev) => ({ ...prev, streamKey: true }));

    copyStreamKeyTimeout.current = setTimeout(() => {
      setCopiedContent((prev) => ({ ...prev, streamKey: false }));
    }, 2000);
  };

  return (
    <Modal
      open={open}
      showLoadingIndicator={loadingRtmps}
      onRequestClose={handleClose}
      customSize={{
        height: '248px',
        minHeight: '200px',
        width: '640px',
        maxWidth: 'calc(100vw - 32px)',
        maxHeight: 'calc(100vh - 64px)',
      }}
      RenderHeaderContent={() => (
        <Container className="flex jc-space-between ai-center" padding="24px 40px 16px 40px">
          <Text tag="h4" className="H4" color="var(--gray-100)">
            Streaming info
          </Text>

          <button className="clear-button padding-4" onClick={handleClose}>
            <Icon name={ICONS.CLOSE} size={16} color="var(--gray-400)" />
          </button>
        </Container>
      )}
    >
      <Container padding="0 40px 24px 40px" className="flex-column gap-16">
        <div>
          <Text className="margin-b-5 margin-l-16 L2" color="var(--gray-400)">
            RTMP Address
          </Text>
          <div className="flex gap-16">
            <MaskedValueContainer value={rtmpInfo.rtmpsAddress} />
            <Button
              className="flex fd-row jc-center ai-center radius-full padding-0"
              width="40px"
              height="40px"
              backgroundColor="var(--gray-600)"
              hoverBackgroundColor="var(--gray-500)"
              onClick={copyRtmpsAddress}
            >
              <Icon
                name={copiedContent.rtmpsAddress ? ICONS.CHECK : ICONS.COPY_FILLED}
                size={12}
                color="var(--gray-200)"
                lightModeColor="var(--gray-1000)"
              />
            </Button>
          </div>
        </div>

        <div>
          <Text className="margin-b-5 margin-l-16 L2" color="var(--gray-400)">
            Stream Key
          </Text>
          <div className="flex gap-16">
            <MaskedValueContainer value={rtmpInfo.streamKey} type={MaskedValueType.PASSWORD} />
            <Button
              className="flex fd-row jc-center ai-center square-40 radius-full padding-0"
              backgroundColor="var(--gray-600)"
              hoverBackgroundColor="var(--gray-500)"
              onClick={copyPassword}
            >
              <Icon
                name={copiedContent.streamKey ? ICONS.CHECK : ICONS.COPY_FILLED}
                size={12}
                color="var(--gray-200)"
                lightModeColor="var(--gray-1000)"
              />
            </Button>
          </div>
        </div>

        {/* TODO: bring this back in once mux streaming times are sorted */}
        {/* <OptionalComponent display={!!rtmpInfo.expiration.trim().length}>
          <div className="width-100-percent margin-t-8">
            <p>
              Credentials valid until <time>{rtmpInfo.expiration}</time>
            </p>
            <p>
              Streaming credentials reset every 4 hours if not in use.
              <br />
              Credentials will never reset during a broadcast / while a stream is active.
            </p>
          </div>
        </OptionalComponent> */}
      </Container>
    </Modal>
  );
};

export default StreamingCredentialsModal;
